import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';

import { Button } from "primereact/button";
import { Dialog } from 'primereact/dialog';
import { ProgressSpinner } from 'primereact/progressspinner';

import { mask, unMask } from 'remask';
import { verificarMaioridadePorData, toastApiError } from '../../../../utils/validacoes.js';
import ContratanteForm, { getPayloadTratado } from './formulario.js';

import classNames from 'classnames';
import { useInfo } from '../../../../context/infoContext.js';
import { ApiAdimplencia } from '../../../../services/api.js';
import { addHours } from 'date-fns';

/**
 * Retorna true quando o documento atual é CPF (11 dígitos).
 *
 * @param {string} maskedDoc
 * @returns {boolean}
 */
function isCpfDocumento(maskedDoc) {
  return unMask(maskedDoc || '').length === 11;
}

export default function ContratanteEditar({ close, itemInfo, reload }) {
  const { darkMode, credorData } = useInfo();
  const [loading, setLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [confirmarEditar, setConfirmarEditar] = useState(false);
  const [gruposOptions, setGruposOptions] = useState([]);

  const loadGrupos = async () => {
    if (!credorData?.credor_id) return;
    try {
      const { data } = await ApiAdimplencia.get('/credorgrupo/buscar');
      const lista = Array.isArray(data) ? data : (data?.dados || data) || [];
      const sorted = [...lista].sort((a, b) =>
        (a.grupo_descricao || '').localeCompare(b.grupo_descricao || '', 'pt-BR')
      );
      setGruposOptions(
        sorted.map((g) => ({ label: g.grupo_descricao, value: g.credor_grupo_id }))
      );
    } catch {
      setGruposOptions([]);
    }
  };

  useEffect(() => {
    loadGrupos();
  }, [credorData?.credor_id]);

  const [contratanteData, setContratanteData] = useState({
    usuario_id: '',
    contratante_id: '',
    endereco_id: '',
    usuario_nome: '',
    usuario_documento: '',
    usuario_data_nascimento: null,
    contratante_email: '',
    contratante_telefone: '',
    endereco_rua: '',
    endereco_numero: '',
    endereco_complemento: '',
    endereco_bairro: '',
    endereco_cidade: '',
    endereco_estado: '',
    endereco_cep: '',
    beneficiario: '',
    contratante_numero: '',
    credor_grupo_id: null
  });

  useEffect(() => {
    getContratanteInfo(itemInfo.usuario_documento, itemInfo.contratante_id);
  }, [itemInfo]);

  async function getContratanteInfo(document, contratanteId) {
    setIsLoading(true);
    try {
      const { data } = await ApiAdimplencia.get(`/contratante/${contratanteId}`);
      const dados = data.dados;
      const { usuario, endereco } = dados;

      setContratanteData({
        contratante_id: dados.contratante_id,
        contratante_numero: dados.contratante_numero || '',
        credor_grupo_id: dados.credor_grupo_id || null,
        usuario_id: usuario.usuario_id,
        usuario_nome: usuario.usuario_nome || '',
        usuario_documento: mask(unMask(usuario.usuario_documento), ['999.999.999-99', '99.999.999/9999-99']),
        contratante_email: usuario.usuario_email || '',
        usuario_data_nascimento: usuario.usuario_data_nascimento
          ? addHours(new Date(usuario.usuario_data_nascimento), 3)
          : null,
        contratante_telefone: usuario.usuario_telefone || '',
        beneficiario: dados.beneficiario || '',
        endereco_id: endereco?.endereco_id || '',
        endereco_cep: mask(endereco?.endereco_cep || '', ['99999-999']),
        endereco_rua: endereco?.endereco_rua || '',
        endereco_numero: endereco?.endereco_numero || '',
        endereco_complemento: endereco?.endereco_complemento || '',
        endereco_bairro: endereco?.endereco_bairro || '',
        endereco_cidade: endereco?.endereco_cidade || '',
        endereco_estado: endereco?.endereco_estado || '',
      });
    } catch (error) {
      console.error(error?.response);
      toast.error('Houve um erro ao recuperar os dados do cliente.');
    }
    setIsLoading(false);
  }

  const handleSubmit = (e) => {
    e.preventDefault();

    // Data SEMPRE obrigatória (CPF e CNPJ)
    if (!contratanteData.usuario_data_nascimento) {
      toast.error('Informe a data de nascimento/criação.');
      return;
    }

    // Verificação de maioridade somente para CPF
    const isCpf = isCpfDocumento(contratanteData.usuario_documento);
    if (isCpf && !verificarMaioridadePorData(contratanteData.usuario_data_nascimento)) {
      toast.error('O devedor deve ser maior de idade.');
      return;
    }

    setConfirmarEditar(true);
  };

  const editContratante = async () => {
    setLoading(true);
    try {
      const payload = getPayloadTratado(contratanteData);

      await ApiAdimplencia.put(`contratante/${contratanteData.contratante_id}`, payload);
      setConfirmarEditar(false);
      toast.success('Cliente atualizado com sucesso.');
      close();
      reload();
    } catch (err) {
      setConfirmarEditar(false);
      toastApiError(err, 'Houve um erro ao atualizar o cliente.');
    } finally {
      setLoading(false);
    }
  };

  const footerDialog = (
    <div className="ml-2 flex justify-content-between">
      <div className="">
        <Button
          severity="info"
          label="Sim"
          icon="pi pi-check"
          loading={loading}
          onClick={editContratante}
        />
      </div>
      <div className="">
        <Button
          severity="danger"
          label="Não"
          icon="pi pi-times"
          loading={loading}
          onClick={() => setConfirmarEditar(false)}
        />
      </div>
    </div>
  );

  return (
    <>
      {isLoading && (
        <Dialog visible closable={false} style={{ width: '20vw', maxHeight: '40vh' }} className="Progress-Spinner-Custom" onHide="">
          <ProgressSpinner strokeWidth='3' />
          <p>Aguarde</p>
        </Dialog>
      )}
      {confirmarEditar && (
        <Dialog
          header="Deseja editar os dados do cliente?"
          onHide={() => setConfirmarEditar(false)}
          visible
          footer={footerDialog}
        />
      )}
      <Dialog
        header="Editar Cliente"
        onHide={close}
        visible
        pt={{
          root: 'w-screen md:w-[70vw] max-h-[100vh]',
          header: classNames(
            'flex items-center justify-between shrink-0',
            'bg-white text-gray-800 border-t-0  rounded-tl-lg rounded-tr-lg p-6',
            'dark:bg-gray-900  dark:text-white/80'
          ),
          content: ({ state }) => ({
            className: classNames(
              'overflow-auto w-full max-h-[85vh]',
              'bg-white text-gray-700 px-6 pb-8 pt-0',
              'rounded-bl-lg rounded-br-lg',
              'dark:bg-gray-900 dark:text-white/80 ',
              { "grow": state.maximized }
            )
          }),
        }}
      >
        <div className="card">
          <div className="w-full">
            <ContratanteForm
              formData={contratanteData}
              setFormData={setContratanteData}
              onSubmit={handleSubmit}
              isLoading={isLoading}
              darkMode={darkMode}
              isEditMode={true}
              gruposOptions={gruposOptions}
              loadGrupos={loadGrupos}
            />
          </div>
        </div>
      </Dialog>
    </>
  );
}
